import React, { useCallback } from 'react';
import { SnackbarMessage, useSnackbar as useNotistack, OptionsObject } from 'notistack';
import { CoreMessages } from 'locale';
import { Button } from '@utmost.co/utmost-design-system/src/components/Button/Button';
import { useIntlFormat } from '../intl/useIntlFormat';

export const useSnackbar = () => {
  const { enqueueSnackbar: queueNotistack, closeSnackbar } = useNotistack();
  const format = useIntlFormat();

  const enqueueSnackbar = useCallback(
    (message: SnackbarMessage, options?: OptionsObject, values?: any) => {
      const defaults: OptionsObject = {
        preventDuplicate: true,
        action: (key: string | number) => (
          <Button
            dataTest="snackbar__notification__button"
            color="default"
            size="small"
            onClick={() => closeSnackbar(key)}
          >
            {format('general.DISMISS')}
          </Button>
        )
      };

      queueNotistack(format(message as CoreMessages, values), { ...defaults, ...options });
    },
    [closeSnackbar, format, queueNotistack]
  );

  const showError = useCallback(
    (message: SnackbarMessage, vars?: any) => {
      enqueueSnackbar(message, { variant: 'error' }, vars);
    },
    [enqueueSnackbar]
  );

  const showSuccess = useCallback(
    (message: SnackbarMessage, descriptionOrVars?: string | any, vars?: any) => {
      if (typeof descriptionOrVars === 'string') {
        queueNotistack(
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <strong>{message as string}</strong>
            <div>{descriptionOrVars}</div>
          </div>,
          { variant: 'success', preventDuplicate: true }
        );
      } else {
        enqueueSnackbar(message, { variant: 'success' }, descriptionOrVars ?? vars);
      }
    },
    [enqueueSnackbar, queueNotistack]
  );

  const showInfo = useCallback(
    (message: SnackbarMessage, vars?: any) => {
      enqueueSnackbar(message, { variant: 'info' }, vars);
    },
    [enqueueSnackbar]
  );

  const showWarning = useCallback(
    (message: SnackbarMessage, vars?: any) => {
      enqueueSnackbar(message, { variant: 'warning' }, vars);
    },
    [enqueueSnackbar]
  );

  return {
    enqueueSnackbar,
    showError,
    showSuccess,
    showInfo,
    showWarning,
    closeSnackbar
  };
};
